// KnowledgeBaseTab.jsx
// Admin-Tab für die HejVi Domain Knowledge Base.
// Nur sichtbar für user.group === 0 (Super-Admin).
//
// Einbindung in AdminPanel.js:
//   import KnowledgeBaseTab from './KnowledgeBaseTab';
//   // Im Tab-Array:
//   { id: 'knowledge', label: '🧠 Wissensbasis', component: <KnowledgeBaseTab makeAuthenticatedRequest={makeAuthenticatedRequest} /> }

import { useState, useEffect, useCallback } from 'react';

// ─── Such-Helper ───────────────────────────────────────────────────────────────
function useSearch() {
  const [q, setQ] = useState('');
  const matches   = useCallback((terms) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return terms.some(t => String(t ?? '').toLowerCase().includes(needle));
  }, [q]);
  return { q, setQ, matches };
}

function SearchBar({ q, setQ, placeholder = 'Suchen…', count, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14, pointerEvents: 'none' }}>🔍</span>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '7px 12px 7px 32px', fontSize: 13, outline: 'none',
          }}
        />
        {q && (
          <span
            onClick={() => setQ('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}
          >×</span>
        )}
      </div>
      {q && (
        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
          {count} / {total}
        </span>
      )}
    </div>
  );
}

// ─── API-Helfer ────────────────────────────────────────────────────────────────
const useKnowledgeApi = (makeAuthenticatedRequest) => {
  const call = useCallback(async (method, path, body = null, isFormData = false) => {
    const opts = { method };
    if (body && !isFormData) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    } else if (body && isFormData) {
      opts.body = body; // FormData
    }
    return makeAuthenticatedRequest(`/domain-knowledge/${path}`, opts);
  }, [makeAuthenticatedRequest]);

  return { call };
};

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function KnowledgeBaseTab({ makeAuthenticatedRequest }) {
  const [activeSection, setActiveSection] = useState('manufacturers');
  const [toast, setToast]                 = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const sections = [
    { id: 'manufacturers', label: '🏭 Hersteller'   },
    { id: 'object-types',  label: '📦 Objekt-Typen' },
    { id: 'trades',        label: '🔧 Gewerke'       },
    { id: 'suggestions',   label: '💡 Vorschläge'    },
    { id: 'import',        label: '📥 Import'        },
  ];

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Abschnitts-Navigation */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            style={{
              padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 500,
              background: activeSection === s.id ? '#2563eb' : '#f1f5f9',
              color:      activeSection === s.id ? '#fff'     : '#374151',
            }}
          >{s.label}</button>
        ))}
      </div>

      {/* Abschnitt-Inhalt */}
      {activeSection === 'manufacturers' && <ManufacturersSection makeReq={makeAuthenticatedRequest} showToast={showToast} />}
      {activeSection === 'object-types'  && <ObjectTypesSection  makeReq={makeAuthenticatedRequest} showToast={showToast} />}
      {activeSection === 'trades'        && <TradesSection        makeReq={makeAuthenticatedRequest} showToast={showToast} />}
      {activeSection === 'suggestions'   && <SuggestionsSection  makeReq={makeAuthenticatedRequest} showToast={showToast} />}
      {activeSection === 'import'        && <ImportSection        makeReq={makeAuthenticatedRequest} showToast={showToast} />}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#16a34a' : '#dc2626',
          color: '#fff', padding: '10px 18px', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,.18)', fontSize: 14,
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// ─── Hersteller ───────────────────────────────────────────────────────────────
function ManufacturersSection({ makeReq, showToast }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [editId, setEditId]               = useState(null);
  const [showAdd, setShowAdd]             = useState(false);
  const [newAlias, setNewAlias]           = useState({});
  const { q, setQ, matches }              = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/manufacturers');
      setManufacturers(data.manufacturers ?? []);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const deleteMfr = async (id) => {
    if (!window.confirm('Hersteller und alle Aliases löschen?')) return;
    await makeReq(`/domain-knowledge/manufacturers/${id}`, { method: 'DELETE' });
    showToast('Hersteller gelöscht');
    load();
  };

  const addAlias = async (mfrId) => {
    const alias = (newAlias[mfrId] ?? '').trim();
    if (!alias) return;
    await makeReq(`/domain-knowledge/manufacturers/${mfrId}/aliases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alias }),
    });
    setNewAlias(a => ({ ...a, [mfrId]: '' }));
    showToast('Alias hinzugefügt');
    load();
  };

  const deleteAlias = async (aliasId) => {
    await makeReq(`/domain-knowledge/manufacturers/aliases/${aliasId}`, { method: 'DELETE' });
    showToast('Alias entfernt');
    load();
  };

  const toggleVerified = async (mfr) => {
    await makeReq(`/domain-knowledge/manufacturers/${mfr.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verified: !mfr.verified }),
    });
    load();
  };

  if (loading) return <Spinner />;

  const filtered = manufacturers.filter(mfr =>
    matches([mfr.canonical_name, mfr.description, mfr.source, ...mfr.aliases.map(a => a.alias)])
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Hersteller ({manufacturers.length})</h3>
        <Btn onClick={() => setShowAdd(true)} primary>+ Hersteller</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Hersteller oder Alias suchen…" count={filtered.length} total={manufacturers.length} />

      {showAdd && (
        <AddManufacturerForm
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Hersteller angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 && q && (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
        )}
        {filtered.map(mfr => (
          <div key={mfr.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>

            {/* Zeile 1: Name+Badge links, Buttons rechts — Buttons niemals umbrechen */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              {/* Links: Name + Badges — dürfen umbrechen */}
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{mfr.canonical_name}</span>
                <span style={{
                  fontSize: 11, padding: '2px 7px', borderRadius: 99,
                  background: mfr.verified ? '#dcfce7' : '#fef9c3',
                  color:      mfr.verified ? '#15803d' : '#92400e',
                }}>{mfr.verified ? '✓ Verifiziert' : '⏳ Ausstehend'}</span>
                <span style={{ fontSize: 11, color: '#cbd5e1' }}>{mfr.source} #{mfr.id}</span>
              </div>
              {/* Rechts: Buttons — immer zusammen, nie umbrechen */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, paddingTop: 1 }}>
                <Btn small onClick={() => toggleVerified(mfr)}>
                  {mfr.verified ? 'Unverifizieren' : 'Verifizieren'}
                </Btn>
                <Btn small danger onClick={() => deleteMfr(mfr.id)}>Löschen</Btn>
              </div>
            </div>

            {/* Zeile 2: Aliases */}
            <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
              {mfr.aliases.map(a => (
                <span key={a.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: a.source === 'ai' ? '#ede9fe' : '#e0f2fe',
                  color:      a.source === 'ai' ? '#5b21b6' : '#0369a1',
                  borderRadius: 99, fontSize: 12, padding: '2px 9px',
                }}>
                  {a.alias}
                  <span style={{ cursor: 'pointer', opacity: .5, lineHeight: 1 }} onClick={() => deleteAlias(a.id)}>×</span>
                </span>
              ))}
              <input
                value={newAlias[mfr.id] ?? ''}
                onChange={e => setNewAlias(a => ({ ...a, [mfr.id]: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addAlias(mfr.id)}
                placeholder="+ Alias"
                style={{ border: '1px dashed #cbd5e1', borderRadius: 99, padding: '2px 10px', fontSize: 12, width: 110, outline: 'none' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddManufacturerForm({ makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [desc, setDesc]       = useState('');
  const [aliases, setAliases] = useState('');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/manufacturers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        canonical_name: name.trim(),
        description:    desc.trim() || null,
        aliases:        aliases.split(',').map(s => s.trim()).filter(Boolean),
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Neuer Hersteller</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Kanonischer Name (z.B. bito)" style={inputStyle} />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Kurzbeschreibung (optional)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases kommagetrennt (z.B. bito AG, Bito Aktiengesellschaft)" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Objekt-Typen ─────────────────────────────────────────────────────────────
function ObjectTypesSection({ makeReq, showToast }) {
  const [objectTypes, setObjectTypes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState(null);
  const [variants, setVariants]       = useState({});
  const [showAdd, setShowAdd]         = useState(false);
  const [showAddVariant, setShowAddVariant] = useState(null);
  const { q, setQ, matches }          = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/object-types');
      setObjectTypes(data.object_types ?? []);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const loadVariants = async (typeId) => {
    if (variants[typeId]) { setExpandedId(typeId); return; }
    const data = await makeReq(`/domain-knowledge/object-types/${typeId}/variants`);
    setVariants(v => ({ ...v, [typeId]: data.variants ?? [] }));
    setExpandedId(typeId);
  };

  const deleteType = async (id) => {
    if (!window.confirm('Objekt-Typ und alle Varianten löschen?')) return;
    await makeReq(`/domain-knowledge/object-types/${id}`, { method: 'DELETE' });
    showToast('Objekt-Typ gelöscht');
    load();
  };

  const deleteVariant = async (typeId, variantId) => {
    await makeReq(`/domain-knowledge/variants/${variantId}`, { method: 'DELETE' });
    showToast('Variante gelöscht');
    const data = await makeReq(`/domain-knowledge/object-types/${typeId}/variants`);
    setVariants(v => ({ ...v, [typeId]: data.variants ?? [] }));
  };

  if (loading) return <Spinner />;

  const filtered = objectTypes.filter(ot =>
    matches([ot.name, ot.manufacturer_name, ot.trade_name, ot.description, ...(ot.aliases ?? [])])
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Objekt-Typen ({objectTypes.length})</h3>
        <Btn primary onClick={() => setShowAdd(true)}>+ Objekt-Typ</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Typ, Hersteller oder Alias suchen…" count={filtered.length} total={objectTypes.length} />

      {showAdd && (
        <AddObjectTypeForm
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Objekt-Typ angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 && q && (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
        )}
        {filtered.map(ot => (
          <div key={ot.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>

            {/* Zeile 1: Expand + Name+Tags links — Buttons rechts */}
            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Links: klickbar zum Aufklappen */}
              <div
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer' }}
                onClick={() => expandedId === ot.id ? setExpandedId(null) : loadVariants(ot.id)}
              >
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{expandedId === ot.id ? '▼' : '▶'}</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{ot.name}</span>
                {ot.manufacturer_name && <span style={{ fontSize: 12, color: '#2563eb' }}>{ot.manufacturer_name}</span>}
                {ot.trade_name && <Tag>{ot.trade_name}</Tag>}
                {(ot.aliases ?? []).map((a, i) => <Tag key={i} muted>{a}</Tag>)}
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{ot.variant_count} Var.</span>
              </div>
              {/* Rechts: Aktionen — immer fixiert */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Btn small onClick={e => { e.stopPropagation(); setShowAddVariant(ot.id); }}>+ Variante</Btn>
                <Btn small danger onClick={e => { e.stopPropagation(); deleteType(ot.id); }}>Löschen</Btn>
              </div>
            </div>

            {/* Varianten (aufgeklappt) */}
            {expandedId === ot.id && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 14px 12px 28px', background: '#fff' }}>
                {showAddVariant === ot.id && (
                  <AddVariantForm
                    makeReq={makeReq}
                    objectTypeId={ot.id}
                    onSaved={async () => {
                      setShowAddVariant(null);
                      const data = await makeReq(`/domain-knowledge/object-types/${ot.id}/variants`);
                      setVariants(v => ({ ...v, [ot.id]: data.variants ?? [] }));
                      showToast('Variante angelegt');
                    }}
                    onCancel={() => setShowAddVariant(null)}
                  />
                )}
                {(variants[ot.id] ?? []).length === 0
                  ? <span style={{ fontSize: 13, color: '#94a3b8' }}>Keine Varianten</span>
                  : (variants[ot.id] ?? []).map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 2 }}>
                        {(v.aliases ?? []).map((a, i) => <Tag key={i} muted>{a}</Tag>)}
                        {Object.entries(v.attributes ?? {}).map(([k, val]) => (
                          <Tag key={k}>{k}: {val}</Tag>
                        ))}
                      </div>
                      <Btn small danger onClick={() => deleteVariant(ot.id, v.id)}>×</Btn>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddObjectTypeForm({ makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [mfr, setMfr]         = useState('');
  const [aliases, setAliases] = useState('');
  const [desc, setDesc]       = useState('');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/object-types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:        name.trim(),
        description: desc.trim() || null,
        aliases:     aliases.split(',').map(s => s.trim()).filter(Boolean),
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Neuer Objekt-Typ</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name}    onChange={e => setName(e.target.value)}    placeholder="Name (z.B. Porsche 911)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases kommagetrennt (Elfer, Neunelfer, 911er)" style={inputStyle} />
        <input value={desc}    onChange={e => setDesc(e.target.value)}    placeholder="Beschreibung (optional)" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

function AddVariantForm({ makeReq, objectTypeId, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [aliases, setAliases] = useState('');
  const [attrs, setAttrs]     = useState('');  // JSON-String
  const [saving, setSaving]   = useState(false);
  const [attrErr, setAttrErr] = useState('');

  const save = async () => {
    if (!name.trim()) return;
    let parsedAttrs = {};
    if (attrs.trim()) {
      try { parsedAttrs = JSON.parse(attrs); } catch { setAttrErr('Ungültiges JSON'); return; }
    }
    setSaving(true);
    await makeReq(`/domain-knowledge/object-types/${objectTypeId}/variants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:       name.trim(),
        aliases:    aliases.split(',').map(s => s.trim()).filter(Boolean),
        attributes: parsedAttrs,
        verified:   true,
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <input value={name}    onChange={e => setName(e.target.value)}    placeholder="Variantenname (z.B. G-Modell Targa 2.7)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases (G-Modell, G-Serie)" style={inputStyle} />
        <input value={attrs}   onChange={e => { setAttrs(e.target.value); setAttrErr(''); }}
          placeholder='Attribute als JSON: {"Baujahr_von":"1974","Motor":"2.7"}' style={inputStyle} />
        {attrErr && <span style={{ color: '#dc2626', fontSize: 12 }}>{attrErr}</span>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? '…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Gewerke ──────────────────────────────────────────────────────────────────
function TradesSection({ makeReq, showToast }) {
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { q, setQ, matches }  = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/trades');
      setTrades(data.trades ?? []);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const deleteTrade = async (id) => {
    if (!window.confirm('Gewerk löschen?')) return;
    await makeReq(`/domain-knowledge/trades/${id}`, { method: 'DELETE' });
    showToast('Gewerk gelöscht');
    load();
  };

  if (loading) return <Spinner />;

  // Baum-Struktur aufbauen
  const roots    = trades.filter(t => !t.parent_id);
  const children = (parentId) => trades.filter(t => String(t.parent_id) === String(parentId));

  // Bei aktiver Suche: flache gefilterte Liste statt Baum
  const flatFiltered = q ? trades.filter(t => matches([t.name, t.name_en])) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Gewerke ({trades.length})</h3>
        <Btn primary onClick={() => setShowAdd(!showAdd)}>+ Gewerk</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Gewerk oder Branche suchen…" count={flatFiltered.length} total={trades.length} />

      {showAdd && (
        <AddTradeForm
          trades={trades}
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Gewerk angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {/* Suchergebnis: flache Liste */}
      {q ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {flatFiltered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
          )}
          {flatFiltered.map(t => {
            const parent = trades.find(p => String(p.id) === String(t.parent_id));
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px' }}>
                {parent && <span style={{ fontSize: 11, color: '#94a3b8' }}>{parent.name} →</span>}
                <span style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</span>
                {t.name_en && <span style={{ fontSize: 12, color: '#64748b' }}>({t.name_en})</span>}
                <Btn small danger style={{ marginLeft: 'auto' }} onClick={() => deleteTrade(t.id)}>Löschen</Btn>
              </div>
            );
          })}
        </div>
      ) : (
        /* Normaler Baum */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {roots.map(branche => (
            <div key={branche.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{branche.name}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Branche</span>
                <Btn small danger style={{ marginLeft: 'auto' }} onClick={() => deleteTrade(branche.id)}>Löschen</Btn>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {children(branche.id).map(gewerk => (
                  <span key={gewerk.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: '#e0f2fe', color: '#0369a1',
                    borderRadius: 99, fontSize: 12, padding: '3px 12px',
                  }}>
                    {gewerk.name}
                    <span style={{ cursor: 'pointer', opacity: .6 }} onClick={() => deleteTrade(gewerk.id)}>×</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddTradeForm({ trades, makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [nameEn, setNameEn]   = useState('');
  const [parentId, setParent] = useState('');
  const [saving, setSaving]   = useState(false);

  const roots = trades.filter(t => !t.parent_id);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/trades', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim(), name_en: nameEn.trim() || null, parent_id: parentId ? Number(parentId) : null }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Neues Gewerk / Branche</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name}   onChange={e => setName(e.target.value)}   placeholder="Name (Deutsch)"  style={inputStyle} />
        <input value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="Name (Englisch, optional)" style={inputStyle} />
        <select value={parentId} onChange={e => setParent(e.target.value)} style={inputStyle}>
          <option value="">— Branche (kein Elternelement) —</option>
          {roots.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? '…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── KI-Vorschläge ────────────────────────────────────────────────────────────
function SuggestionsSection({ makeReq, showToast }) {
  const [suggestions, setSuggestions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [canonicalNames, setCanonicals] = useState({});  // id → string

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/suggestions');
      setSuggestions(data.suggestions ?? []);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const canonical = (canonicalNames[id] ?? '').trim();
    if (!canonical) { showToast('Kanonischen Namen eingeben', 'error'); return; }
    await makeReq(`/domain-knowledge/suggestions/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ canonical_name: canonical }),
    });
    showToast('Vorschlag bestätigt ✓');
    load();
  };

  const reject = async (id) => {
    await makeReq(`/domain-knowledge/suggestions/${id}/reject`, { method: 'POST' });
    showToast('Vorschlag abgelehnt');
    load();
  };

  if (loading) return <Spinner />;

  if (suggestions.length === 0) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>✓ Keine offenen Vorschläge</div>;
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: 16 }}>Offene KI-Vorschläge ({suggestions.length})</h3>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Diese Hersteller/Entitäten wurden von der KI neu erkannt und warten auf deine Bestätigung.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {suggestions.map(s => {
          const data = (() => { try { return JSON.parse(s.parsed_json); } catch { return {}; } })();
          return (
            <div key={s.id} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {data.type === 'manufacturer' ? '🏭 Hersteller' : '📦 Entität'}: <em>{s.raw_input}</em>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                    Normalisiert: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{data.normalized}</code>
                    &nbsp;·&nbsp;{new Date(s.created_at).toLocaleDateString('de-DE')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={canonicalNames[s.id] ?? data.suggested_canonical ?? ''}
                    onChange={e => setCanonicals(c => ({ ...c, [s.id]: e.target.value }))}
                    placeholder="Kanonischer Name"
                    style={{ ...inputStyle, width: 180, padding: '5px 10px' }}
                  />
                  <Btn primary onClick={() => approve(s.id)}>Bestätigen</Btn>
                  <Btn danger  onClick={() => reject(s.id)}>Ablehnen</Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Import ───────────────────────────────────────────────────────────────────
function ImportSection({ makeReq, showToast }) {
  const [mode, setMode]         = useState('text');  // 'text' | 'document'
  const [text, setText]         = useState('');
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [imported, setImported] = useState(null);

  const analyze = async () => {
    setLoading(true);
    setPreview(null);
    setImported(null);
    try {
      let data;
      if (mode === 'text') {
        data = await makeReq('/domain-knowledge/import/text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, save: false }),
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('save', '0');
        data = await makeReq('/domain-knowledge/import/document', { method: 'POST', body: fd });
      }
      setPreview(data.preview);
    } catch (e) { showToast('Analyse fehlgeschlagen', 'error'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      let data;
      if (mode === 'text') {
        data = await makeReq('/domain-knowledge/import/text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, save: true, preview_json: preview }),
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('save', '1');
        fd.append('preview_json', JSON.stringify(preview));
        data = await makeReq('/domain-knowledge/import/document', { method: 'POST', body: fd });
      }
      setImported(data.stats);
      setPreview(null);
      showToast('Import erfolgreich ✓');
    } catch { showToast('Import fehlgeschlagen', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: 16 }}>Wissen importieren</h3>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Gib Rohtext ein oder lade ein Dokument hoch (TXT, CSV, PDF, DOCX).
        HejVi analysiert den Inhalt per KI und extrahiert Hersteller, Objekt-Typen und Varianten.
      </p>

      {/* Modus-Auswahl */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Btn primary={mode === 'text'}     onClick={() => setMode('text')}>✏️ Rohtext</Btn>
        <Btn primary={mode === 'document'} onClick={() => setMode('document')}>📄 Dokument</Btn>
      </div>

      {mode === 'text' && (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={`Beispiele:\n\nHersteller: bito (auch: bito AG, Bito Aktiengesellschaft) — Farbenhersteller\n\nPorsche 911 (auch: Elfer, Neunelfer)\nVarianten:\n- F-Modell (1963–1973)\n- G-Modell (1973–1989): Karosserie: Coupe/Targa/Cabrio, Motor: 2.7/3.0/3.2\n- 964 (1989–1994)\n- 993 (1993–1998)\n\nGewerke: KFZ-Technik, Maler und Lackierer`}
          style={{ ...inputStyle, height: 220, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
        />
      )}

      {mode === 'document' && (
        <div
          style={{
            border: '2px dashed #cbd5e1', borderRadius: 10, padding: 30,
            textAlign: 'center', cursor: 'pointer', background: '#f8fafc',
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById('kb-file-input').click()}
        >
          <input id="kb-file-input" type="file" accept=".txt,.csv,.md,.pdf,.docx" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0])} />
          {file
            ? <span style={{ color: '#2563eb', fontWeight: 500 }}>📄 {file.name}</span>
            : <span style={{ color: '#94a3b8' }}>Datei hierher ziehen oder klicken<br /><small>TXT · CSV · MD · PDF · DOCX</small></span>
          }
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn primary onClick={analyze} disabled={loading || (mode === 'text' ? !text.trim() : !file)}>
          {loading ? '⏳ Analysiere…' : '🔍 KI analysieren'}
        </Btn>
        {preview && <Btn onClick={save} disabled={loading} style={{ background: '#16a34a', color: '#fff' }}>💾 In Wissensbasis speichern</Btn>}
      </div>

      {/* Vorschau */}
      {preview && <ImportPreview preview={preview} />}

      {/* Ergebnis */}
      {imported && (
        <div style={{ marginTop: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 8 }}>✓ Import abgeschlossen</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {Object.entries(imported).map(([k, v]) => (
              <div key={k} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>{v}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImportPreview({ preview }) {
  const { manufacturers = [], object_types = [], variants = [], trades = [] } = preview;

  return (
    <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>🔍 Vorschau — erkannte Entitäten</div>

      {manufacturers.length > 0 && (
        <PreviewGroup label="🏭 Hersteller" count={manufacturers.length}>
          {manufacturers.map((m, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{m.canonical_name}</strong>
              {m.aliases?.length > 0 && <> → Aliases: {m.aliases.join(', ')}</>}
              {m.description && <span style={{ color: '#64748b' }}> · {m.description}</span>}
            </div>
          ))}
        </PreviewGroup>
      )}

      {object_types.length > 0 && (
        <PreviewGroup label="📦 Objekt-Typen" count={object_types.length}>
          {object_types.map((o, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{o.name}</strong>
              {o.manufacturer && <span style={{ color: '#2563eb' }}> ({o.manufacturer})</span>}
              {o.aliases?.length > 0 && <> · Aliases: {o.aliases.join(', ')}</>}
            </div>
          ))}
        </PreviewGroup>
      )}

      {variants.length > 0 && (
        <PreviewGroup label="🔖 Varianten" count={variants.length}>
          {variants.map((v, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{v.object_type}</strong> → {v.name}
              {v.aliases?.length > 0 && <> ({v.aliases.join(', ')})</>}
              {v.attributes && Object.keys(v.attributes).length > 0 && (
                <span style={{ color: '#64748b' }}> · {Object.entries(v.attributes).map(([k,val]) => `${k}: ${val}`).join(', ')}</span>
              )}
            </div>
          ))}
        </PreviewGroup>
      )}

      {trades.length > 0 && (
        <PreviewGroup label="🔧 Gewerke" count={trades.length}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {trades.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </div>
        </PreviewGroup>
      )}

      {manufacturers.length + object_types.length + variants.length + trades.length === 0 && (
        <div style={{ color: '#92400e', fontSize: 13 }}>⚠ Keine Entitäten erkannt. Bitte Text anpassen.</div>
      )}
    </div>
  );
}

function PreviewGroup({ label, count, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>{label} ({count})</div>
      {children}
    </div>
  );
}

// ─── Micro-Komponenten ────────────────────────────────────────────────────────
const inputStyle = {
  border: '1px solid #e2e8f0', borderRadius: 7, padding: '7px 12px',
  fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none',
};

function Btn({ children, onClick, primary, danger, small, disabled, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding:       small ? '4px 10px' : '8px 16px',
        borderRadius:  8,
        border:        'none',
        cursor:        disabled ? 'not-allowed' : 'pointer',
        fontSize:      small ? 12 : 13,
        fontWeight:    500,
        background:    disabled ? '#e2e8f0' : primary ? '#2563eb' : danger ? '#dc2626' : '#f1f5f9',
        color:         disabled ? '#94a3b8' : primary || danger ? '#fff' : '#374151',
        opacity:       disabled ? .7 : 1,
        whiteSpace:    'nowrap',
        ...style,
      }}
    >{children}</button>
  );
}

function Tag({ children, muted }) {
  return (
    <span style={{
      background:   muted ? '#f1f5f9' : '#dbeafe',
      color:        muted ? '#64748b' : '#1d4ed8',
      borderRadius: 99, fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Spinner() {
  return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>⏳ Lädt…</div>;
}
